import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/prop-types */
import { CloseIcon } from '@icons';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { CircularProgress } from '..';
import { useFirstRender } from '../common/effects/useFirstRender';
import { isDark } from '../common/utils';
import styles from './input.module.scss';
const Input = React.forwardRef((props, ref) => {
    const { RTL = false, accent = 'flat', border = false, children, controlled = true, disabled = false, enableClear = true, focusable = true, id = '', isAutoComplete = false, noUniqueId = false, onChange, onFocus, onKeyUp, onKeyDown, placeholder = 'Please enter a value ...', showSpinner = false, size = 'sm', state = 'default', style, type = 'text', value = '', transparentBg = false, alignCenter = false, min = 0, max = Number.MAX_VALUE, maxLength = Number.MAX_VALUE, } = props;
    const [inputValue, setInputValue] = useState(value);
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const isFirstRender = useFirstRender();
    const inputId = useRef(noUniqueId ? id : nanoid());
    const [hasFocus, setHasFocus] = useState(false);
    useImperativeHandle(ref, () => ({
        focus() {
            inputRef.current?.focus();
        },
        getValue() {
            return inputRef.current?.value || '';
        },
        setValue(val) {
            setInputValue(val);
        },
    }));
    /**
     * Handler for clearing the input value
     */
    const handleClear = useCallback((ev) => {
        ev.preventDefault();
        setInputValue('');
        if (inputRef.current) {
            inputRef.current.focus();
        }
        onChange?.('');
    }, []);
    /**
     * Handler for handling the input change
     */
    const handleInput = useCallback((ev) => {
        if (controlled) {
            const val = ev.target.value.slice(0, maxLength);
            setInputValue(val);
            onChange?.(val);
        }
    }, [controlled, inputValue]);
    const handleUnControlled = useCallback((ev) => {
        const val = ev.target.value;
        onChange?.(val);
    }, []);
    const clearClass = useMemo(() => classNames([
        styles.clear,
        styles.icon,
        !inputValue ? styles.hidden : '',
    ]), [inputValue]);
    const isDarkMode = useMemo(() => isDark(), []);
    const inputClass = useMemo(() => classNames(styles.input, {
        [styles[state]]: true,
        [styles.border]: border,
        [styles.disabled]: disabled,
        [styles.focus]: hasFocus,
        [styles['no_icon']]: !children,
        [styles.rtl]: RTL,
        [styles[accent]]: true,
        [styles[size]]: true,
        [styles.transparent_bg_color]: transparentBg,
        [styles.input_dark]: isDarkMode,
        [styles.no_clear]: !enableClear,
    }), [disabled, hasFocus, isDarkMode]);
    const inputTextClass = useMemo(() => classNames(styles.input_text, isDarkMode ? styles.input_dark : '', alignCenter ? styles.align_center : '', children ? styles.with_icon : '', RTL ? styles.RTL : ''), []);
    useEffect(() => {
        if (controlled && !isFirstRender.current) {
            setInputValue(value);
        }
    }, [value]);
    const controlledProps = controlled
        ? {
            onChange: handleInput,
            value: inputValue,
        }
        : { onChange: handleUnControlled };
    const autoCompleteProps = useMemo(() => isAutoComplete
        ? {
            'aria-controls': id,
            role: 'combobox',
        }
        : null, [isAutoComplete]);
    const focusProps = useMemo(() => {
        if (focusable) {
            return {
                onBlur: () => {
                    setHasFocus(false);
                },
                onFocus: (ev) => {
                    // setHasFocus(true);
                    onFocus?.(ev);
                },
            };
        }
        else {
            // alert("kunj")
            return {};
        }
    }, [focusable, hasFocus]);
    const numProps = useMemo(() => {
        if (type === 'number') {
            return {
                max,
                min,
            };
        }
        else {
            return {};
        }
    }, []);
    const inputDisabled = useMemo(() => {
        return disabled;
    }, [disabled]);
    const clearProps = useMemo(() => ({
        'aria-hidden': !showSpinner && !inputValue,
        'aria-label': 'clear input',
        hidden: !showSpinner && !inputValue,
    }), [inputValue, showSpinner]);
    const handleKeyUp = (ev) => {
        if (!hasFocus) {
            setHasFocus(true);
            inputRef.current?.focus();
        }
        onKeyUp?.(ev);
    };
    return (_jsxs("div", { className: inputClass, role: "textbox", ref: containerRef, style: style, "aria-label": placeholder, ...autoCompleteProps, children: [children && _jsx("span", { className: styles.icon, children: children }), _jsx("input", { type: type, placeholder: placeholder, ...focusProps, ...controlledProps, ...numProps, onKeyUp: handleKeyUp, onKeyDown: onKeyDown, ref: inputRef, id: inputId.current, disabled: inputDisabled, className: inputTextClass }), !showSpinner && (_jsx("span", { onMouseDown: handleClear, className: clearClass, role: "button", "data-testid": "rc-clear-input", ...clearProps, children: enableClear && _jsx(CloseIcon, {}) })), showSpinner && (_jsx("span", { role: "img", "data-testid": "rc-input-spinner", children: _jsx(CircularProgress, { size: "xs" }) }))] }));
});
Input.displayName = 'Input';
export { Input };
